import {
	ItemSubtitle,
	ItemTitle,
	ItemValue,
	ListItem,
	StyledFooter,
	StyledHeader,
	StyledList,
	SubtitleRow,
	TextContainer,
} from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/ValuationTargetNodeList/styled';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatDecimal } from '@dealstack/shared/lib/format/number';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	makeComplexDataTestId,
	VALUATION_ELEMENT_ID,
} from '@dealstack/shared/tests/testElementIds';
import React from 'react';
import { ValuationInputDetails } from '@dealstack/frontend/app/pages/Valuations/shared/valuationInputDetails.types';

interface Props {
	className?: string;
	valuationInputDetails: ValuationInputDetails[];
	selectedValuationInputId: string | null;
	setSelectedValuationInputId: (id: string) => void;
	currentNumberFactor: number;
}

export const ValuationTargetNodeList: React.FC<Props> = ({
	valuationInputDetails,
	selectedValuationInputId,
	setSelectedValuationInputId,
	currentNumberFactor,
}) => {
	const totalEquityValue = valuationInputDetails.reduce(
		(acc, curr) => acc.plus(curr.equityValue),
		new HighPrecisionDecimal(0)
	);

	const tValuations = useTranslationValuations();

	const formatNumberWithFactor = (value: HighPrecisionDecimal): string => {
		return formatDecimal({
			value: value.div(currentNumberFactor),
			maxPrecision: 2,
			minPrecision: 2,
			lockPrecision: true,
			returnNumericalZero: true,
		});
	};

	return (
		<StyledList data-testid={VALUATION_ELEMENT_ID.NODE_LIST}>
			<StyledHeader>
				<TextV2 variant="sans/14/600">
					{tValuations('target_nodes.valuation_target_nodes_title')}
				</TextV2>
			</StyledHeader>
			<div>
				{valuationInputDetails.map(({ id, name, equityValue }) => (
					<ListItem
						key={id}
						onClick={() => setSelectedValuationInputId(id)}
						isSelected={selectedValuationInputId === id}
						data-testid={makeComplexDataTestId(VALUATION_ELEMENT_ID.TARGET_NODE, name)}
					>
						<TextContainer>
							<ItemTitle>{name}</ItemTitle>
							<SubtitleRow>
								<ItemSubtitle>
									{tValuations('target_nodes.equity_value_field_label')}
								</ItemSubtitle>
								<ItemValue
									data-testid={makeComplexDataTestId(
										VALUATION_ELEMENT_ID.TARGET_NODE_EQUITY_VALUE,
										name
									)}
								>
									{formatNumberWithFactor(equityValue)}
								</ItemValue>
							</SubtitleRow>
						</TextContainer>
					</ListItem>
				))}
			</div>
			<StyledFooter>
				<TextV2 variant="sans/14/600">
					{tValuations('target_nodes.total_equity_value_title')}
				</TextV2>
				<TextV2 data-testid={VALUATION_ELEMENT_ID.TOTAL_EQUITY_VALUE} variant="sans/14/600">
					{formatNumberWithFactor(totalEquityValue)}
				</TextV2>
			</StyledFooter>
		</StyledList>
	);
};
